import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Volume2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Button, Card, ErrorText, Input, Label, Select } from "@/components/ui";
import { api } from "@/lib/api";
import { voice } from "@/lib/voice/controller";

interface Endpoint {
  id: string;
  name: string;
}

interface AdminModel {
  endpoint_id: string;
  model_id: string;
}

interface VoiceConfig {
  stt: { endpoint_id: string | null; model: string; language: string };
  tts: { endpoint_id: string | null; model: string; default_voice: string; voices: string[]; response_format: string; sample_rate: number; speed: number };
  min_speech_seconds: number;
}

export default function VoiceAdmin() {
  const qc = useQueryClient();
  const cfg = useQuery({ queryKey: ["admin", "voice"], queryFn: () => api.get<VoiceConfig>("/api/admin/voice") });
  const endpoints = useQuery({ queryKey: ["admin", "endpoints"], queryFn: () => api.get<Endpoint[]>("/api/admin/endpoints") }).data ?? [];
  const models = useQuery({ queryKey: ["admin", "models"], queryFn: () => api.get<AdminModel[]>("/api/admin/models") }).data ?? [];
  const [form, setForm] = useState<VoiceConfig | null>(null);
  const [voices, setVoices] = useState("");
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (cfg.data && !form) {
      setForm(cfg.data);
      setVoices(cfg.data.tts.voices.join(", "));
    }
  }, [cfg.data, form]);
  const save = useMutation({
    mutationFn: () => api.put("/api/admin/voice", { ...form, tts: { ...form!.tts, voices: voices.split(",").map((v) => v.trim()).filter(Boolean) } }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["voice-status"] });
    },
  });
  if (!form) return null;
  const stt = (patch: Partial<VoiceConfig["stt"]>) => setForm({ ...form, stt: { ...form.stt, ...patch } });
  const tts = (patch: Partial<VoiceConfig["tts"]>) => setForm({ ...form, tts: { ...form.tts, ...patch } });
  const modelList = (endpointId: string | null) => models.filter((m) => m.endpoint_id === endpointId).map((m) => m.model_id);
  function submit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    save.mutate();
  }

  return (
    <form onSubmit={submit} className="space-y-6">
      <Card title="Speech to text">
        <p className="mb-3 text-sm text-zinc-500">An OpenAI-compatible /audio/transcriptions endpoint (Whisper and similar). Turns on the microphone button in chats.</p>
        <div className="grid gap-4 sm:grid-cols-3">
          <Label label="Endpoint">
            <Select value={form.stt.endpoint_id ?? ""} onChange={(e) => stt({ endpoint_id: e.target.value || null })}>
              <option value="">Off</option>
              {endpoints.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </Select>
          </Label>
          <Label label="Model">
            <Input list="stt-models" value={form.stt.model} onChange={(e) => stt({ model: e.target.value })} />
            <datalist id="stt-models">
              {modelList(form.stt.endpoint_id).map((m) => (
                <option key={m} value={m} />
              ))}
            </datalist>
          </Label>
          <Label label="Language" hint="e.g. en; empty = detect">
            <Input value={form.stt.language} onChange={(e) => stt({ language: e.target.value })} />
          </Label>
        </div>
        <div className="mt-4 max-w-xs">
          <Label label="Minimum speech (seconds)" hint="Clips with less detected speech are ignored (0 = off).">
            <Input type="number" step="0.1" min={0} value={form.min_speech_seconds} onChange={(e) => setForm({ ...form, min_speech_seconds: Number(e.target.value) })} />
          </Label>
        </div>
      </Card>
      <Card title="Text to speech">
        <p className="mb-3 text-sm text-zinc-500">An OpenAI-compatible /audio/speech endpoint (Kokoro, Pocket TTS…). Replies are spoken in voice mode, and messages get a speaker button.</p>
        <div className="grid gap-4 sm:grid-cols-3">
          <Label label="Endpoint">
            <Select value={form.tts.endpoint_id ?? ""} onChange={(e) => tts({ endpoint_id: e.target.value || null })}>
              <option value="">Off</option>
              {endpoints.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </Select>
          </Label>
          <Label label="Model">
            <Input list="tts-models" value={form.tts.model} onChange={(e) => tts({ model: e.target.value })} />
            <datalist id="tts-models">
              {modelList(form.tts.endpoint_id).map((m) => (
                <option key={m} value={m} />
              ))}
            </datalist>
          </Label>
          <Label label="Default voice">
            <Input value={form.tts.default_voice} onChange={(e) => tts({ default_voice: e.target.value })} />
          </Label>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-4">
          <Label label="Voices users can pick" hint="Comma separated">
            <Input value={voices} onChange={(e) => setVoices(e.target.value)} />
          </Label>
          <Label label="Format">
            <Select value={form.tts.response_format} onChange={(e) => tts({ response_format: e.target.value })}>
              {["pcm", "wav", "mp3", "opus", "flac"].map((f) => (
                <option key={f}>{f}</option>
              ))}
            </Select>
          </Label>
          <Label label="Sample rate (pcm)">
            <Input type="number" value={form.tts.sample_rate} onChange={(e) => tts({ sample_rate: Number(e.target.value) })} />
          </Label>
          <Label label="Speed">
            <Input type="number" step="0.05" value={form.tts.speed} onChange={(e) => tts({ speed: Number(e.target.value) })} />
          </Label>
        </div>
      </Card>
      <ErrorText error={save.error} />
      <div className="flex items-center gap-3">
        <Button disabled={save.isPending}>Save</Button>
        <Button type="button" variant="secondary" onClick={() => voice.readAloud("Hello! This is how I sound.")} disabled={!saved && !cfg.data?.tts.endpoint_id}>
          <Volume2 size={16} /> Test voice
        </Button>
        {saved && <span className="text-sm text-green-600">Saved.</span>}
      </div>
    </form>
  );
}
